/**
 * Local Wikipedia-style lore article: Exolot Dominion (faction).
 * Content is authored here — not loaded from the database.
 */
const exolotDominionArticle = {
  title: 'Exolot Dominion',
  image: '/factions/exolot.png',
  format: 'wiki',
  text: `The **Exolot Dominion** was a highly advanced interstellar civilization composed of a long-lived species with a liquid metal interior known as the **Exolots**. Organized around a rigid life-cycle rather than hereditary class structures, Dominion society emphasized collective purpose, military service, expansionism, and the belief in Exolot superiority over other forms of life.

---

## Biology

Exolots were mostly metallic lifeforms that did not suffer from disease and possessed a fixed lifespan of exactly **520 years**. At the end of life, their bodies ceased all movement and permanently solidified into metallic statues that were either kept to remember their greatness or recycled to serve more purpose in death as they did in life.

Their society revolved around three major life stages:

- **Urruna** (0-300 years): a military caste characterized by extreme group loyalty, honor culture, and a willingness to sacrifice themselves for the Dominion.
- **Exolots** (300-480 years): the mature adult stage, marked by increased individuality, self-awareness, and intellectual development.
- **Juggernauts** or **Mentora** (480-520 years): the final transformation. Most individuals became Juggernauts, elite warriors and cultural heroes. A small minority instead became Mentora, highly intelligent leaders, scientists, and rulers.

Reproduction occurs exclusively between Juggernauts and the Mentora. Mentora inspired strong reproductive instincts in all adult Exolots despite being biologically incompatible with them. This is believed to be one of the major reasons unity and peace could be kept so reliably.

---

## Society and Culture

The Dominion valued:

- Honor
- Military achievement
- Unity
- Service to the state
- Personal sacrifice

Military heroes, especially Juggernauts, were celebrated throughout society and functioned as cultural icons. Individuals approaching their final transformation were honored in elaborate public ceremonies.

Despite its rhetoric of unity, Dominion society was highly hierarchical. The Mentora held political, scientific, and administrative authority, while the Urruna were often deliberately kept uninformed about larger strategic matters.

The Dominion also maintained a number of genetically or artificially engineered specialist beings known as **Experiments**, such as the bodyguard construct **Borran** and the war hero **Wengol Ussek**.

---

## Government

The Dominion was governed by **High Command**, a council of three Mentora and government staff of further Mentora and sometimes Exolots. Decisions affecting entire species and worlds were concentrated within this leadership class.

Communication among Exolots relied on flashing patterns of light through their two to twelve eyes rather than spoken language. They were capable of hearing but could not naturally communicate vocally.

---

## Foreign Policy

The Dominion was aggressively expansionist and frequently encountered alien civilizations. Their typical approach to contact followed a predictable doctrine:

1. Identify a potential threat.
2. Assess its danger.
3. Deploy Urruna forces to neutralize or subjugate it.

Many species became vassals under Dominion rule. Dominion culture regarded political provocation and displays of superiority as normal diplomatic behavior.

This arrogance ultimately contributed to one of the greatest disasters in Dominion history.

---

## First Contact with Tessel 8

The Dominion's fateful encounter with the world known as **Tessel 8** began as a scientific expedition led by the Mentora **Kana Sarek**.

Unbeknownst to the Exolots, Tessel 8 had already been assimilated by **Asrah**, a hive-mind virus that controlled the native population through biological infection and telepathic networking.

During first contact, Asrah infiltrated the Dominion through the engineered protector Borran, seized control of the expedition fleet, and gained access to Exolot technology, particularly the Exolot's impressive **space folding drives**.

---

## War Against Asrah

The resulting conflict became an existential war for the Dominion.

Asrah rapidly expanded using conquered vassal populations and enormous numbers of infected hosts. Although unable to directly assimilate Exolots due to their unusual metallic physiology, Asrah developed indirect methods to weaken Dominion society.

The war revealed several weaknesses within the Dominion:

- Overconfidence toward other species.
- Dependence on centralized leadership.
- Belief in Exolot intellectual superiority.
- A willingness to sacrifice individuals for strategic goals.

---

## The Mentora Crisis

Asrah eventually exploited a previously unknown vulnerability connected to the Exolots' dependence on **lavender-derived crystalline compounds** used for neural stability.

By infecting Mentora through a sophisticated parasitic agent, Asrah triggered widespread psychological instability among the Dominion's ruling class and effectively doomed the entire species, since without the Mentora they were no longer able to reproduce. Furthermore it was decided to no longer let anyone ascend, since the risk to end up Mentora was too high. This effectively reduced Exolot life expectancy to **480 years** and sealed the fate of the Dominion. The age of the **Last Generation** had begun.
`,
};

export default exolotDominionArticle;
